import { useState, useRef } from 'react'
import { useCourseTitles, useFlatCurriculumActions, type CourseTitleSummary, type LicenceClass } from '@/hooks/useFlatCurriculum'
import { SecureAssetViewer } from '@/components/curriculum/SecureAssetViewer'
import { AssetThumbnail } from '@/components/curriculum/AssetThumbnail'
import { sectionStyle, pageTitleStyle, pageSubtitleStyle } from './adminListStyles'

const MAX_VIDEO_UPLOAD_BYTES = 4 * 1024 * 1024

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
  font: '600 13.5px Inter, sans-serif',
  color: active ? '#fff' : '#1B2A4A',
  background: active ? '#1B2A4A' : 'transparent',
})

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0',
  borderBottom: '1px solid rgba(27,42,74,0.07)', flexWrap: 'wrap',
}

const slotStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
  borderRadius: 8, background: 'rgba(27,42,74,0.04)', flex: '1 1 260px',
}

const smallButtonStyle: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)',
  background: 'transparent', font: '500 12px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer',
}

type PreviewTarget = { assetId: string; assetType: 'pdf' | 'video'; title: string }

// Flat course-title authoring: pick a licence class, type a title, upload one PDF and
// one video for it. Behind the scenes this still orchestrates the real Course -> Version
// -> Module -> Lesson -> Asset chain the assignment/in-car-unlock workflow depends on
// (src/hooks/useFlatCurriculum.ts) — "Replace" forks a new draft version, carries the
// unchanged file forward via a server-side Storage copy (no re-upload needed), and
// "Publish" is the only point any of it becomes visible. The superseded version is never
// archived (so a student already assigned against it keeps access) — see
// useFlatCurriculum.ts's own module comment for why.
export default function AdminCurriculum() {
  const [licenceClass, setLicenceClass] = useState<LicenceClass>('G2')
  const { courses, loading, error, refresh } = useCourseTitles(licenceClass)
  const { createCourseTitle, ensureDraftLesson, startReplace, uploadAsset, publishDraft } = useFlatCurriculumActions()
  const [preview, setPreview] = useState<PreviewTarget | null>(null)

  const [newTitle, setNewTitle] = useState('')
  const [addingCourse, setAddingCourse] = useState(false)
  const [formError, setFormError] = useState('')

  const handleAddCourse = async () => {
    if (!newTitle.trim()) return
    setFormError('')
    setAddingCourse(true)
    try {
      const r = await createCourseTitle(licenceClass, newTitle.trim())
      if (!r.ok) { setFormError(r.error ?? 'Could not create course title.'); return }
      setNewTitle('')
      await refresh()
    } finally {
      setAddingCourse(false)
    }
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={pageTitleStyle}>E-learning</div>
      <div style={pageSubtitleStyle}>
        Course titles for each licence class. Upload a PDF and a video for each one — uploading again replaces it
        once you publish.
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        <div style={tabStyle(licenceClass === 'G2')} onClick={() => setLicenceClass('G2')}>G2</div>
        <div style={tabStyle(licenceClass === 'G')} onClick={() => setLicenceClass('G')}>G</div>
      </div>

      {error && <div className='banner-error' style={{ marginBottom: 14 }}>{error}</div>}

      <div style={sectionStyle}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
          <input
            className='field-input'
            style={{ flex: 1 }}
            placeholder={`New ${licenceClass} course title`}
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
          />
          <button className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }} disabled={addingCourse} onClick={handleAddCourse}>
            {addingCourse ? 'Creating…' : '+ Add course title'}
          </button>
        </div>
        {formError && <div className='banner-error'>{formError}</div>}
      </div>

      <div style={sectionStyle}>
        {loading ? (
          <div style={{ color: 'rgba(27,42,74,0.4)' }}>Loading…</div>
        ) : courses.length === 0 ? (
          <div style={{ color: 'rgba(27,42,74,0.4)' }}>No {licenceClass} course titles yet.</div>
        ) : (
          courses.map(c => (
            <CourseRow
              key={c.courseId}
              course={c}
              onEnsureDraftLesson={ensureDraftLesson}
              onStartReplace={startReplace}
              onUploadAsset={uploadAsset}
              onPublish={publishDraft}
              onChanged={refresh}
              onPreview={setPreview}
            />
          ))
        )}
      </div>

      {preview && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.6)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
          onClick={() => setPreview(null)}
        >
          <div style={{ width: '100%', maxWidth: 820 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ font: '600 14px Inter, sans-serif', color: '#fff' }}>{preview.title}</div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <SecureAssetViewer assetId={preview.assetId} assetType={preview.assetType} title={preview.title} />
          </div>
        </div>
      )}
    </div>
  )
}

function CourseRow({
  course, onEnsureDraftLesson, onStartReplace, onUploadAsset, onPublish, onChanged, onPreview,
}: {
  course: CourseTitleSummary
  onEnsureDraftLesson: ReturnType<typeof useFlatCurriculumActions>['ensureDraftLesson']
  onStartReplace: ReturnType<typeof useFlatCurriculumActions>['startReplace']
  onUploadAsset: ReturnType<typeof useFlatCurriculumActions>['uploadAsset']
  onPublish: ReturnType<typeof useFlatCurriculumActions>['publishDraft']
  onChanged: () => Promise<void>
  onPreview: (p: PreviewTarget) => void
}) {
  const [rowError, setRowError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  // Tracks which slot ('pdf' | 'video') is mid-upload, or 'publish' — drives the inline
  // "Uploading…" state and, just as importantly, blocks a second click from starting a
  // second concurrent operation on this row (picking PDF then immediately picking video
  // before the first finishes would otherwise race: both would see no draft yet and each
  // independently fork a new version, exactly the scattering bug this screen already hit
  // once). A ref (not state) because it must be read synchronously at the top of
  // handlePick, before any await — state set via setState isn't visible until the next
  // render, which is too late to stop a fast second click.
  const [busySlot, setBusySlot] = useState<'pdf' | 'video' | 'publish' | null>(null)
  const busyRef = useRef<'pdf' | 'video' | 'publish' | null>(null)
  const pdfInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const hasDraft = !!course.draftVersionId
  const canPublish = hasDraft && (!!course.draftPdfAssetId || !!course.draftVideoAssetId)
  const busy = busySlot !== null

  const setBusy = (slot: 'pdf' | 'video' | 'publish' | null) => {
    busyRef.current = slot
    setBusySlot(slot)
  }

  // Ensures a draft lesson exists to upload into, then uploads into it. Three cases,
  // and it's important not to conflate the first two (that conflation was a real bug: it
  // forked a brand-new draft version on every upload instead of reusing the existing one,
  // scattering a course's pdf/video across several never-published versions):
  //   1. A draft version already exists AND already has a lesson (draftLessonId set) —
  //      just upload into it.
  //   2. A draft version already exists but has no lesson yet (a course whose original
  //      draft never got a module — e.g. created before this flat UI, or before the old
  //      "+ Add module" bug was fixed) — fill in that SAME draft, never fork a new one.
  //   3. No draft at all (only a published version, no replace in progress) — fork a new
  //      draft off the published version, carrying both current files forward.
  const handlePick = async (assetType: 'pdf' | 'video', file: File) => {
    if (busyRef.current) return
    setRowError('')
    setSuccessMessage('')
    if (assetType === 'video' && file.size > MAX_VIDEO_UPLOAD_BYTES) {
      setRowError(`Video files are limited to 4 MB (this file is ${(file.size / (1024 * 1024)).toFixed(1)} MB).`)
      return
    }

    setBusy(assetType)
    try {
      let lessonId = course.draftLessonId
      let replacingAssetId = assetType === 'pdf' ? course.draftPdfAssetId : course.draftVideoAssetId

      if (!lessonId && course.draftVersionId) {
        const filled = await onEnsureDraftLesson(course.draftVersionId, course.title)
        if (!filled.ok) { setRowError(filled.error ?? 'Could not prepare the draft.'); return }
        lessonId = filled.lessonId ?? null
      } else if (!lessonId) {
        const started = await onStartReplace(course.courseId, course.title, {
          pdfAssetId: course.pdfAssetId, videoAssetId: course.videoAssetId,
        })
        if (!started.ok) { setRowError(started.error ?? 'Could not start a new version.'); return }
        lessonId = started.lessonId ?? null
        replacingAssetId = assetType === 'pdf' ? course.pdfAssetId : course.videoAssetId
      }

      const result = await onUploadAsset(lessonId!, assetType, file, `${course.title} (${assetType})`, replacingAssetId)
      if (!result.ok) { setRowError(result.error ?? 'Upload failed.'); return }

      setSuccessMessage(`${assetType === 'pdf' ? 'PDF' : 'Video'} uploaded — click Publish to make it live.`)
      await onChanged()
    } finally {
      setBusy(null)
    }
  }

  const handlePublish = async () => {
    if (!course.draftVersionId || busyRef.current) return
    setRowError('')
    setSuccessMessage('')
    setBusy('publish')
    try {
      const r = await onPublish(course.draftVersionId)
      if (!r.ok) { setRowError(r.error ?? 'Could not publish.'); return }
      setSuccessMessage('Published — this is now live.')
      await onChanged()
    } finally {
      setBusy(null)
    }
  }

  const pdfPreviewAssetId = course.draftPdfAssetId ?? course.pdfAssetId
  const videoPreviewAssetId = course.draftVideoAssetId ?? course.videoAssetId

  return (
    <div style={rowStyle}>
      <div style={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A', flex: 1 }}>{course.title}</div>
        {hasDraft && (
          <div style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(212,151,43,0.14)', color: '#9A6C0E', font: '600 11px Inter, sans-serif' }}>
            draft in progress
          </div>
        )}
      </div>

      <div style={slotStyle}>
        <span style={{ font: '500 12.5px Inter, sans-serif', color: course.pdfAssetId ? '#0F7C6C' : 'rgba(27,42,74,0.4)', flex: 1 }}>
          PDF {busySlot === 'pdf' ? '— uploading…' : hasDraft ? (course.draftPdfAssetId ? '(updated, unpublished)' : course.pdfAssetId ? '(unchanged)' : '') : course.pdfAssetId ? '— live' : '— none'}
        </span>
        {pdfPreviewAssetId && (
          <AssetThumbnail
            assetId={pdfPreviewAssetId} assetType='pdf' title={`${course.title} — PDF`} width={56} height={56}
            onClick={() => onPreview({ assetId: pdfPreviewAssetId, assetType: 'pdf', title: `${course.title} — PDF` })}
          />
        )}
        <input ref={pdfInputRef} type='file' accept='application/pdf' style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handlePick('pdf', f); e.target.value = '' }} />
        <button disabled={busy} onClick={() => pdfInputRef.current?.click()} style={smallButtonStyle}>
          {busySlot === 'pdf' ? 'Uploading…' : course.pdfAssetId || course.draftPdfAssetId ? 'Replace PDF' : 'Upload PDF'}
        </button>
      </div>

      <div style={slotStyle}>
        <span style={{ font: '500 12.5px Inter, sans-serif', color: course.videoAssetId ? '#0F7C6C' : 'rgba(27,42,74,0.4)', flex: 1 }}>
          Video {busySlot === 'video' ? '— uploading…' : hasDraft ? (course.draftVideoAssetId ? '(updated, unpublished)' : course.videoAssetId ? '(unchanged)' : '') : course.videoAssetId ? '— live' : '— none'}
        </span>
        {videoPreviewAssetId && (
          <AssetThumbnail
            assetId={videoPreviewAssetId} assetType='video' title={`${course.title} — Video`} width={72} height={48}
            onClick={() => onPreview({ assetId: videoPreviewAssetId, assetType: 'video', title: `${course.title} — Video` })}
          />
        )}
        <input ref={videoInputRef} type='file' accept='video/mp4' style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handlePick('video', f); e.target.value = '' }} />
        <button disabled={busy} onClick={() => videoInputRef.current?.click()} style={smallButtonStyle}>
          {busySlot === 'video' ? 'Uploading…' : course.videoAssetId || course.draftVideoAssetId ? 'Replace video' : 'Upload video'}
        </button>
      </div>

      {hasDraft && (
        <button className='btn-primary' style={{ width: 'auto', padding: '9px 18px' }} disabled={busy || !canPublish} onClick={handlePublish}>
          {busySlot === 'publish' ? 'Publishing…' : 'Publish'}
        </button>
      )}

      {rowError && <div className='banner-error' style={{ flex: '1 1 100%' }}>{rowError}</div>}
      {!rowError && successMessage && (
        <div style={{ flex: '1 1 100%', font: '500 12.5px Inter, sans-serif', color: '#0F7C6C' }}>✓ {successMessage}</div>
      )}
    </div>
  )
}
